/**
 * Newsletter Popup Component
 * Custom element for newsletter subscription popup
 */
class NewsletterPopup extends HTMLElement {
  constructor() {
    super();
    this.dialog = null;
    this.closeButton = null;
    this.config = {};
    this.storageKeys = {
      shown: 'newsletter_popup_shown',
      subscribed: 'newsletter_popup_subscribed',
      pageViews: 'newsletter_popup_page_views',
      dismissed: 'newsletter_popup_dismissed'
    };
  }

  connectedCallback() {
    // Get configuration from data attributes
    this.config = {
      enabled: this.dataset.enabled === 'true',
      displayMode: this.dataset.displayMode,
      delay: parseInt(this.dataset.delay) * 1000, // Convert to milliseconds
      frequency: this.dataset.frequency,
      testMode: this.dataset.testMode === 'true',
      popupFlow: this.dataset.popupFlow,
      position: this.dataset.position,
      closeOnBackdrop: this.dataset.closeOnBackdrop !== 'false'
    };

    // Get DOM references
    this.dialog = this.querySelector('dialog');
    this.closeButton = this.querySelector('[ref="closeButton"]');
    
    if (!this.dialog) {
      console.error('Newsletter popup: Dialog element not found');
      return;
    }

    // Initialize popup functionality
    this.initializeDialog();
    
    // Initialize if conditions are met
    if (this.shouldShowPopup()) {
      this.initializePopup();
    }
  }

  /**
   * Initialize dialog functionality
   */
  initializeDialog() {
    // Close button event
    if (this.closeButton) {
      this.closeButton.addEventListener('click', () => this.closeDialog());
    }

    // Close on backdrop click
    if (this.config.closeOnBackdrop) {
      this.dialog.addEventListener('click', (e) => {
        if (e.target === this.dialog) {
          this.closeDialog();
        }
      });
    }

    // Close on ESC key
    this.dialog.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.closeDialog();
      }
    });

    // Handle form submission
    this.initializeForm();
    
    // Initialize two-step flow if configured
    if (this.config.popupFlow === 'two_step') {
      this.initializeTwoStepFlow();
    }
  }

  /**
   * Check if popup should be shown based on frequency and user history
   */
  shouldShowPopup() {
    console.log('Checking if popup should show...', this.config);
    
    // Always show in test mode
    if (this.config.testMode) {
      console.log('Test mode enabled - showing popup');
      return true;
    }
    
    // Don't show if disabled
    if (!this.config.enabled) {
      console.log('Popup disabled');
      return false;
    }
    
    // Don't show if user already subscribed
    if (localStorage.getItem(this.storageKeys.subscribed)) {
      console.log('User already subscribed');
      return false;
    }
    
    // Check frequency settings
    const lastShown = localStorage.getItem(this.storageKeys.shown);
    const dismissed = localStorage.getItem(this.storageKeys.dismissed);
    
    if (dismissed && this.config.frequency === 'once') {
      console.log('User dismissed popup and frequency is once');
      return false;
    }
    
    if (lastShown) {
      const timeSinceLastShown = Date.now() - parseInt(lastShown);
      
      switch (this.config.frequency) {
        case 'session':
          const sessionShown = sessionStorage.getItem(this.storageKeys.shown);
          console.log('Session check:', !sessionShown);
          return !sessionShown;
        case 'once':
          console.log('Once frequency - already shown');
          return false;
        case 'daily':
          const showDaily = timeSinceLastShown > 24 * 60 * 60 * 1000;
          console.log('Daily check:', showDaily);
          return showDaily;
        case 'weekly':
          const showWeekly = timeSinceLastShown > 7 * 24 * 60 * 60 * 1000;
          console.log('Weekly check:', showWeekly);
          return showWeekly;
        case 'always':
        default:
          console.log('Always frequency');
          return true;
      }
    }
    
    console.log('First time showing popup');
    return true;
  }

  /**
   * Initialize popup based on display mode
   */
  initializePopup() {
    console.log('Initializing popup with mode:', this.config.displayMode);
    
    switch (this.config.displayMode) {
      case 'time':
        this.initTimeDelay();
        break;
      case 'scroll':
        this.initScrollTrigger();
        break;
      case 'exit_intent':
        this.initExitIntent();
        break;
      case 'page_views':
        this.initPageViews();
        break;
      default:
        this.initTimeDelay();
    }
  }

  /**
   * Show popup after time delay
   */
  initTimeDelay() {
    console.log('Setting timeout for', this.config.delay, 'ms');
    setTimeout(() => {
      this.openPopup();
    }, this.config.delay);
  }

  /**
   * Show popup after scrolling percentage
   */
  initScrollTrigger() {
    const scrollHandler = () => {
      const scrollPercentage = (window.scrollY / (document.body.scrollHeight - window.innerHeight)) * 100;
      
      if (scrollPercentage >= this.config.delay) {
        window.removeEventListener('scroll', scrollHandler);
        this.openPopup();
      }
    };
    
    window.addEventListener('scroll', scrollHandler);
  }

  /**
   * Show popup on exit intent
   */
  initExitIntent() {
    let exitIntentTriggered = false;
    
    const exitHandler = (e) => {
      // Detect mouse leaving viewport at the top
      if (e.clientY <= 0 && !exitIntentTriggered) {
        exitIntentTriggered = true;
        this.openPopup();
      }
    };
    
    // Desktop only - mobile doesn't have reliable exit intent
    if (window.matchMedia('(min-width: 768px)').matches) {
      document.addEventListener('mouseout', exitHandler);
    }
  }

  /**
   * Show popup after X page views
   */
  initPageViews() {
    let pageViews = parseInt(localStorage.getItem(this.storageKeys.pageViews) || '0');
    pageViews++;
    localStorage.setItem(this.storageKeys.pageViews, pageViews.toString());
    
    if (pageViews >= this.config.delay) {
      this.openPopup();
      localStorage.removeItem(this.storageKeys.pageViews);
    }
  }

  /**
   * Open the popup and track display
   */
  openPopup() {
    console.log('Opening popup...');
    
    if (!this.dialog) {
      console.error('Dialog not found');
      return;
    }

    // Show dialog
    this.dialog.showModal();
    
    // Add active class to container
    this.classList.add('active');
    
    // Add position class
    this.dialog.classList.add(`newsletter-popup__dialog--${this.config.position}`);
    
    // Lock body scroll
    document.documentElement.setAttribute('scroll-lock', '');
    
    // Track popup shown
    this.trackPopupShown();
    
    console.log('Popup opened successfully');
  }

  /**
   * Close the popup
   */
  closeDialog() {
    console.log('Closing popup...');
    
    if (!this.dialog || !this.dialog.open) return;

    // Add closing animation class
    this.dialog.classList.add('dialog-closing');
    
    // Wait for animation
    setTimeout(() => {
      this.dialog.close();
      this.dialog.classList.remove('dialog-closing');
      
      // Remove active class from container
      this.classList.remove('active');
      
      // Unlock body scroll
      document.documentElement.removeAttribute('scroll-lock');
      
      // Track dismissal
      localStorage.setItem(this.storageKeys.dismissed, 'true');
      
      console.log('Popup closed successfully');
    }, 300);
  }

  /**
   * Track that popup was shown
   */
  trackPopupShown() {
    const now = Date.now().toString();
    localStorage.setItem(this.storageKeys.shown, now);
    sessionStorage.setItem(this.storageKeys.shown, now);
  }

  /**
   * Initialize form submission handling
   */
  initializeForm() {
    const forms = this.querySelectorAll('.newsletter-popup__form');
    
    forms.forEach(form => {
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        await this.handleFormSubmission(form);
      });
    });
  }

  /**
   * Initialize two-step flow controls
   */
  initializeTwoStepFlow() {
    const ctaButton = this.querySelector('[ref="ctaButton"]');
    const introStep = this.querySelector('[ref="introStep"]');
    const formStep = this.querySelector('[ref="formStep"]');
    const backButton = this.querySelector('.newsletter-popup__back');
    
    if (ctaButton && introStep && formStep) {
      ctaButton.addEventListener('click', () => {
        introStep.style.display = 'none';
        formStep.style.display = 'block';
        
        // Focus email input
        setTimeout(() => {
          const emailInput = formStep.querySelector('input[type="email"]');
          if (emailInput) emailInput.focus();
        }, 100);
      });
    }
    
    if (backButton && introStep && formStep) {
      backButton.addEventListener('click', () => {
        formStep.style.display = 'none';
        introStep.style.display = 'block';
      });
    }
  }

  /**
   * Handle form submission
   */
  async handleFormSubmission(form) {
    const submitButton = form.querySelector('button[type="submit"]');
    const originalText = submitButton.textContent;
    
    // Show loading state
    submitButton.disabled = true;
    submitButton.textContent = 'Enviando...';
    
    try {
      const formData = new FormData(form);
      
      // Use fetch to submit to Shopify customer endpoint
      const response = await fetch('/contact', {
        method: 'POST',
        body: formData,
        headers: {
          'Accept': 'application/json, text/plain, */*'
        }
      });
      
      if (response.ok || response.status === 302) {
        // Mark as subscribed
        localStorage.setItem(this.storageKeys.subscribed, 'true');
        
        // Show success message
        this.showSuccessMessage();
        
        // Close popup after delay
        setTimeout(() => {
          this.closeDialog();
        }, 3000);
      } else {
        throw new Error('Subscription failed');
      }
    } catch (error) {
      console.error('Newsletter submission error:', error);
      this.showError('Ha ocurrido un error. Por favor, inténtalo de nuevo.');
      
      // Reset button
      submitButton.disabled = false;
      submitButton.textContent = originalText;
    }
  }

  /**
   * Show success message
   */
  showSuccessMessage() {
    const successStep = this.querySelector('[ref="successStep"]');
    const currentStep = this.querySelector('.newsletter-popup__step:not([style*="display: none"])');
    
    if (successStep && currentStep) {
      currentStep.style.display = 'none';
      successStep.style.display = 'block';
    }
  }

  /**
   * Show error message
   */
  showError(message) {
    // Create or update error message element
    let errorElement = this.querySelector('.newsletter-popup__error');
    
    if (!errorElement) {
      errorElement = document.createElement('div');
      errorElement.className = 'newsletter-popup__error';
      const form = this.querySelector('.newsletter-popup__form');
      if (form) {
        form.insertAdjacentElement('afterbegin', errorElement);
      }
    }
    
    errorElement.textContent = message;
    errorElement.style.display = 'block';
    
    // Hide error after 5 seconds
    setTimeout(() => {
      errorElement.style.display = 'none';
    }, 5000);
  }
}

// Define the custom element
if (!customElements.get('newsletter-popup')) {
  customElements.define('newsletter-popup', NewsletterPopup);
}

// Auto-initialize on DOMContentLoaded if elements exist
document.addEventListener('DOMContentLoaded', () => {
  const popups = document.querySelectorAll('newsletter-popup');
  console.log('Found newsletter popups:', popups.length);
});